import moment from 'moment';
import {
  AGUARDANDO_DIGITACAO,
  EM_DIGITACAO,
  DIGITACAO_FINALIZADA,
  EXECUTADA_GDIS,
} from '../../../constants/status';
import { INSPECAO_MT_BT_TIPO_SERVICO_ID } from '../../../utils';

/**
 * Período de medição: dia 26 do mês anterior (00:00) até dia 25 do mês corrente (fim do dia).
 * A partir do dia 26 do mês corrente, o ciclo passa a ser 26 deste mês → 25 do mês seguinte.
 */
export const getMedicaoPeriod = (ref = moment()) => {
  const m = moment(ref);
  const day = m.date();
  let inicio;
  let fim;
  if (day >= 26) {
    inicio = m.clone().date(26).startOf('day');
    fim = m.clone().add(1, 'month').date(25).endOf('day');
  } else {
    inicio = m.clone().subtract(1, 'month').date(26).startOf('day');
    fim = m.clone().date(25).endOf('day');
  }
  return { inicio: inicio.valueOf(), fim: fim.valueOf() };
};

export const formatMedicaoPeriodLabel = (inicio, fim) => {
  if (inicio == null || fim == null) return '';
  const start = moment(inicio).format('DD/MM/YYYY');
  const end = moment(fim).format('DD/MM/YYYY');
  return `${start} – ${end}`;
};

/** Verifica se o intervalo coincide com o período de medição calculado para hoje. */
export const isMedicaoPeriod = (inicio, fim) => {
  const expected = getMedicaoPeriod();
  return inicio === expected.inicio && fim === expected.fim;
};

/** Ciclo 26→25 imediatamente anterior ao de `ref`. */
export const getPreviousMedicaoPeriod = (ref = moment()) => {
  const current = getMedicaoPeriod(ref);
  return getMedicaoPeriod(moment(current.inicio).subtract(1, 'day'));
};

export const isPreviousMedicaoPeriod = (inicio, fim, ref = moment()) => {
  const expected = getPreviousMedicaoPeriod(ref);
  return inicio === expected.inicio && fim === expected.fim;
};

export const DIGITACAO_STATUS = {
  AGUARDANDO_DIGITACAO,
  EM_DIGITACAO,
  DIGITACAO_FINALIZADA,
  EXECUTADA_GDIS,
};

/** Ordem dos status no pipeline de digitação (usada em gráficos empilhados e na faixa). */
export const STATUS_ORDER = [
  AGUARDANDO_DIGITACAO,
  EM_DIGITACAO,
  DIGITACAO_FINALIZADA,
  EXECUTADA_GDIS,
];

export const STATUS_LABELS = {
  [AGUARDANDO_DIGITACAO]: 'Aguardando Digitação',
  [EM_DIGITACAO]: 'Em Digitação',
  [DIGITACAO_FINALIZADA]: 'Digitação Finalizada',
  [EXECUTADA_GDIS]: 'Executada GDIS',
};

/**
 * Cor semântica por status (chave de paleta do tema MUI).
 * Total = info (teal/azul), Aguardando = info, Em Digitação = warning,
 * Finalizada = success, Executada GDIS = neutro.
 */
export const STATUS_COLOR = {
  TOTAL: 'info',
  [AGUARDANDO_DIGITACAO]: 'info',
  [EM_DIGITACAO]: 'warning',
  [DIGITACAO_FINALIZADA]: 'success',
  [EXECUTADA_GDIS]: 'default',
};

/** Mapeamento status -> classe do chip em renderStatus (cell-renderers/status.js). */
export const statusToChip = (status) => {
  const label = STATUS_LABELS[status] || status;
  if (status === DIGITACAO_FINALIZADA || status === EXECUTADA_GDIS) {
    return { value: 'VALIDADO', label };
  }
  if (status === EM_DIGITACAO) {
    return { value: 'AGUARDANDO', label };
  }
  return { value: 'AGUARDANDO', label };
};

export const STATUS_FILTER_OPTIONS = STATUS_ORDER.map((id) => ({
  id,
  nome: STATUS_LABELS[id],
}));

export const EMPTY_RESUMO = {
  filtrado: {},
  total: {},
};

const isMetric = (obj) =>
  obj &&
  typeof obj === 'object' &&
  ('nsirs' in obj ||
    'servicos' in obj ||
    'servicosTotal' in obj ||
    'servicosDigitados' in obj ||
    'servicosPendentes' in obj);

export const EMPTY_METRIC = {
  nsirs: 0,
  servicos: 0,
  servicosTotal: 0,
  servicosDigitados: 0,
  servicosPendentes: 0,
  hasBreakdown: false,
};

const normalizeMetricRaw = (raw) => {
  if (raw == null) return { ...EMPTY_METRIC };
  if (typeof raw === 'number') {
    return { ...EMPTY_METRIC, nsirs: raw };
  }
  if (!isMetric(raw)) return { ...EMPTY_METRIC };

  const hasBreakdown =
    raw.servicosTotal != null ||
    raw.servicosDigitados != null ||
    raw.servicosPendentes != null;

  const servicosTotal = raw.servicosTotal ?? raw.servicos ?? 0;
  const servicosDigitados = hasBreakdown ? (raw.servicosDigitados ?? 0) : 0;
  let servicosPendentes = hasBreakdown ? raw.servicosPendentes : null;
  if (hasBreakdown && servicosPendentes == null) {
    servicosPendentes = Math.max(0, servicosTotal - servicosDigitados);
  }
  if (!hasBreakdown) {
    servicosPendentes = 0;
  }

  return {
    nsirs: raw.nsirs ?? 0,
    servicos: servicosTotal,
    servicosTotal,
    servicosDigitados,
    servicosPendentes: servicosPendentes ?? 0,
    hasBreakdown,
  };
};

/** Garante o shape { filtrado, total } com mapas status -> métricas por status. */
export const normalizeResumo = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_RESUMO;
  }
  const filtrado =
    data.filtrado && typeof data.filtrado === 'object' ? data.filtrado : {};
  const total = data.total && typeof data.total === 'object' ? data.total : {};
  return { filtrado, total };
};

/** Retorna métricas normalizadas de um status (compatível com API legada). */
export const getMetric = (counts, status) => {
  if (!counts) return { ...EMPTY_METRIC };
  return normalizeMetricRaw(counts[status]);
};

/** Soma métricas de todos os status (card Total). */
export const sumMetrics = (counts) => {
  if (!counts || typeof counts !== 'object') return { ...EMPTY_METRIC };
  return STATUS_ORDER.reduce(
    (acc, status) => {
      const m = getMetric(counts, status);
      return {
        nsirs: acc.nsirs + m.nsirs,
        servicos: acc.servicos + m.servicosTotal,
        servicosTotal: acc.servicosTotal + m.servicosTotal,
        servicosDigitados: acc.servicosDigitados + m.servicosDigitados,
        servicosPendentes: acc.servicosPendentes + m.servicosPendentes,
        hasBreakdown: acc.hasBreakdown || m.hasBreakdown,
      };
    },
    { ...EMPTY_METRIC },
  );
};

/**
 * Body enviado à API: normaliza chaves single/multi do CustomFilterV2
 * (digitadorId, inspetorId, malhaId, statuses) e injeta defaults MT/BT + dateTypes.
 */
export const normalizeFilterForApi = (filter) => {
  if (!filter || typeof filter !== 'object') return {};
  const body = {};

  if (filter.inicio != null) body.inicio = filter.inicio;
  if (filter.fim != null) body.fim = filter.fim;

  body.dateTypes =
    Array.isArray(filter.dateTypes) && filter.dateTypes.length > 0
      ? filter.dateTypes
      : ['DATA_DIGITACAO'];

  const toArray = (v) => (Array.isArray(v) ? v : [v]);

  if (Array.isArray(filter.digitadorIds) && filter.digitadorIds.length > 0) {
    body.digitadorIds = filter.digitadorIds;
  } else if (filter.digitadorId != null && filter.digitadorId !== '') {
    body.digitadorIds = toArray(filter.digitadorId);
  }

  if (Array.isArray(filter.inspetorIds) && filter.inspetorIds.length > 0) {
    body.inspetorIds = filter.inspetorIds;
  } else if (filter.inspetorId != null && filter.inspetorId !== '') {
    body.inspetorIds = toArray(filter.inspetorId);
  }

  if (Array.isArray(filter.malhaIds) && filter.malhaIds.length > 0) {
    body.malhaIds = filter.malhaIds;
  } else if (filter.malhaId != null && filter.malhaId !== '') {
    body.malhaIds = toArray(filter.malhaId);
  }

  if (filter.gerenciaId) body.gerenciaId = filter.gerenciaId;
  if (filter.poloId) body.poloId = filter.poloId;
  if (filter.subestacaoId) body.subestacaoId = filter.subestacaoId;
  if (filter.alimentadorId) body.alimentadorId = filter.alimentadorId;
  if (filter.codigo) body.codigo = filter.codigo;

  if (Array.isArray(filter.statuses) && filter.statuses.length > 0) {
    body.statuses = filter.statuses;
  }

  body.tipoServicos =
    Array.isArray(filter.tipoServicos) && filter.tipoServicos.length > 0
      ? filter.tipoServicos
      : [INSPECAO_MT_BT_TIPO_SERVICO_ID];

  if (filter.status) body.status = filter.status;

  return body;
};
